import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, ArrowLeft, Receipt, UploadSimple } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

/**
 * v1.21.0 — Full-page global search. Searches both slips (by UTR) and
 * ledger transactions (by reference, payment mode, amount).
 */
export default function GlobalSearch() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const run = async () => {
        if (q.trim().length < 3) { toast.error("Type at least 3 characters"); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/search/global", { params: { q: q.trim() } });
            setData(data);
            setSearchParams({ q: q.trim() }, { replace: true });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        if (q.trim().length >= 3) run();
        // eslint-disable-next-line
    }, []);

    const slips = data?.slips || [];
    const txns = data?.txns || [];
    const total = slips.length + txns.length;

    const openSlip = (u) => navigate(`/admin/uploads?utr=${encodeURIComponent(u.utr || "")}&_open=${Date.now()}&status=all`);
    const openTxn = (t) => navigate(`/admin/reports?partyId=${t.party_id}`);

    return (
        <div className="space-y-6" data-testid="global-search-page">
            <div>
                <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                    <ArrowLeft size={12} /> Back to Dashboard
                </Link>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Global Search</h1>
                <p className="mt-1 text-sm text-slate-600">Search slips (by UTR) and ledger transactions (by reference, payment mode, or amount) across every party.</p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                        <MagnifyingGlass size={16} className="text-slate-400" />
                        <input
                            data-testid="global-search-input"
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && run()}
                            placeholder="UTR · reference · amount · payment mode…"
                            className="flex-1 border-none bg-transparent text-sm outline-none placeholder:text-slate-400"
                        />
                    </div>
                    <button onClick={run} disabled={loading || q.trim().length < 3} data-testid="global-search-btn"
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                        {loading ? "Searching…" : "Search"}
                    </button>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                    Tip: type a numeric amount to find exact-amount payments · type a UTR fragment to find matching slips
                </div>
            </div>

            {data && (
                <div className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-900">{total}</span> result{total === 1 ? "" : "s"} for <span className="font-mono">"{q}"</span>
                    <span className="ml-2">· <span className="font-semibold">{slips.length}</span> slip{slips.length === 1 ? "" : "s"}</span>
                    <span className="ml-2">· <span className="font-semibold">{txns.length}</span> transaction{txns.length === 1 ? "" : "s"}</span>
                </div>
            )}

            {slips.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
                        <UploadSimple size={18} className="text-blue-700" />
                        <div className="font-display text-sm font-semibold text-slate-900">Payment slips</div>
                        <div className="text-[11px] text-slate-500">({slips.length})</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Uploaded</th>
                                    <th className="px-3 py-2 text-left">Party</th>
                                    <th className="px-3 py-2 text-left">UTR</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {slips.map((u) => (
                                    <tr key={u.id} data-testid={`gs-slip-${u.id}`} onClick={() => openSlip(u)} className="cursor-pointer hover:bg-blue-50/40">
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700">{u.payment_date || u.uploaded_at?.slice(0, 10)}</td>
                                        <td className="px-3 py-2 text-slate-900">{u.party_name || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{u.utr || "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(u.amount)}</td>
                                        <td className="px-3 py-2"><StatusBadge status={u.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {txns.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
                        <Receipt size={18} className="text-emerald-700" />
                        <div className="font-display text-sm font-semibold text-slate-900">Transactions</div>
                        <div className="text-[11px] text-slate-500">({txns.length})</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Party</th>
                                    <th className="px-3 py-2 text-left">Type</th>
                                    <th className="px-3 py-2 text-left">Reference</th>
                                    <th className="px-3 py-2 text-left">Mode</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {txns.map((t) => (
                                    <tr key={t.id} data-testid={`gs-txn-${t.id}`} onClick={() => openTxn(t)} className="cursor-pointer hover:bg-emerald-50/40">
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{dmY(t.date)}</td>
                                        <td className="px-3 py-2 text-slate-900">{t.party_name || "—"}</td>
                                        <td className="px-3 py-2"><span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase bg-slate-100 text-slate-700">{t.txn_type}</span></td>
                                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{t.reference || t.utr || "—"}</td>
                                        <td className="px-3 py-2 text-xs text-slate-700">{t.payment_mode || "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(t.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {data && total === 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm">
                    <MagnifyingGlass size={36} className="mx-auto mb-2 text-slate-300" />
                    <div className="font-display text-lg font-semibold text-slate-700">No matches for "{q}"</div>
                    <div className="mt-1 text-sm text-slate-500">Try a shorter fragment of the UTR, or an exact amount.</div>
                </div>
            )}
        </div>
    );
}
